/**
 * Configuración Global de la Aplicación
 * 
 * Archivo centralizado para todos los parámetros configurables del frontend
 * Patrón similar a appsettings.json del backend
 * 
 * IMPORTANTE: Todos los tiempos están en milisegundos
 */

// ========================================
// CONFIGURACIÓN DE SESIÓN
// ========================================

export const CONFIGURACION_SESION = {
    /**
     * Intervalo de verificación de expiración de token (5 segundos)
     * El hook useSessionManager verifica cada X tiempo si el token expiró
     */
    INTERVALO_VERIFICACION_TOKEN: 5 * 1000,

    /**
     * Tiempo antes de expiración para mostrar advertencia (2 minutos)
     * Modal ModalAdvertenciaSesion aparece cuando quedan X minutos
     */
    ADVERTENCIA_ANTES_EXPIRACION: 2,  // minutos

    /**
     * Duración del countdown en modal de advertencia (120 segundos = 2 minutos)
     * Auto-logout cuando countdown llega a 0 (coincide con expiración token)
     */
    DURACION_COUNTDOWN_SESSION: 60,  // segundos (1 minuto)
} as const;

// ========================================
// CONFIGURACIÓN DE INACTIVIDAD
// ========================================

export const CONFIGURACION_INACTIVIDAD = {
    /**
     * Tiempo total de inactividad antes de logout (10 minutos)
     * Usuario inactivo por X tiempo → se cierra sesión automáticamente
     */
    TIEMPO_INACTIVIDAD_TOTAL: 10 * 60 * 1000,  // ✅ 10 minutos (sincronizado con backend)

    /**
     * Tiempo antes de logout para mostrar advertencia (20 segundos)
     * Modal ModalAdvertenciaInactividad aparece 20 segundos antes
     */
    ADVERTENCIA_ANTES_LOGOUT_INACTIVIDAD: 20 * 1000,  //20 segundos en ms

    /**
     * Eventos que detectan actividad del usuario
     * Cualquiera de estos eventos resetea el timer de inactividad
     */
    EVENTOS_ACTIVIDAD: ['mousedown', 'keydown', 'scroll', 'touchstart'] as const,
} as const;

// ========================================
// CONFIGURACIÓN DE SESIÓN MÚLTIPLE
// ========================================

export const CONFIGURACION_SESION_MULTIPLE = {
    /**
     * Intervalo de heartbeat local (BroadcastChannel) (5 segundos)
     * Verificación entre tabs del mismo browser
     */
    INTERVALO_HEARTBEAT_LOCAL: 5 * 1000,  // 5 segundos

    /**
     * Clave localStorage para deviceId persistente
     */
    CLAVE_STORAGE_DEVICE_ID: 'zafiro-device-id',
} as const;

// ========================================
// CONFIGURACIÓN DE TABS
// ========================================

export const CONFIGURACION_TABS = {
    /**
     * Número máximo de tabs que se pueden abrir simultáneamente
     * (tabs anclados no cuentan para el límite)
     */
    MAX_TABS: 10,

    /**
     * Permitir tabs anclados
     */
    PERMITIR_ANCLADOS: true,
} as const;

// ========================================
// CONFIGURACIÓN DE BLOQUEO MANUAL
// ========================================

export const CONFIGURACION_BLOQUEO = {
    /**
     * Clave localStorage donde se guarda estado de bloqueo
     * Permite persistir bloqueo entre refreshes/navegación
     */
    CLAVE_STORAGE_BLOQUEO: 'zafiro-lock-storage',
} as const;

// ========================================
// CONFIGURACIÓN DE API
// ========================================

export const CONFIGURACION_API = {
    /**
     * Timeout para requests HTTP (30 segundos)
     * Axios cancelará requests que excedan este tiempo
     */
    TIMEOUT_REQUEST: 30 * 1000,

    /**
     * Tiempo de retry para requests fallidos (3 segundos)
     */
    DELAY_RETRY: 3 * 1000,

    /**
     * Número máximo de reintentos para requests fallidos
     */
    MAX_REINTENTOS: 3,
} as const;

// ========================================
// CONFIGURACIÓN DE UI/UX
// ========================================

export const CONFIGURACION_UI = {
    /**
     * Duración de toasts de notificación (3 segundos)
     */
    DURACION_TOAST: 3000,

    /**
     * Delay de animaciones (300ms)
     */
    DELAY_ANIMACION: 300,

    /**
     * Timeout de debounce para búsquedas (500ms)
     */
    DEBOUNCE_BUSQUEDA: 500,
} as const;

// ========================================
// VALORES CALCULADOS (No editar directamente)
// ========================================

/**
 * Tiempo de actividad antes de mostrar advertencia inactividad
 * Calculado automáticamente: TOTAL - ADVERTENCIA
 */
export const TIEMPO_ACTIVIDAD_CALCULADO =
    CONFIGURACION_INACTIVIDAD.TIEMPO_INACTIVIDAD_TOTAL -
    CONFIGURACION_INACTIVIDAD.ADVERTENCIA_ANTES_LOGOUT_INACTIVIDAD;

// ========================================
// TIPO PARA INTELLISENSE
// ========================================

export type ConfiguracionSesion = typeof CONFIGURACION_SESION;
export type ConfiguracionInactividad = typeof CONFIGURACION_INACTIVIDAD;
export type ConfiguracionBloqueo = typeof CONFIGURACION_BLOQUEO;
export type ConfiguracionAPI = typeof CONFIGURACION_API;
export type ConfiguracionUI = typeof CONFIGURACION_UI;
